import React, { useState } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  FileText,
  Settings,
  DollarSign,
  Calendar,
  Users,
  CheckSquare,
  Plus,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import projectTemplateMasterService from '../../services/projectTemplateMasterService'

const ProjectTemplateMasterForm = () => {
  const navigate = useNavigate()
  const [showHelp, setShowHelp] = useState(false)
  const [activeTab, setActiveTab] = useState('basicInfo')
  const [formData, setFormData] = useState({
    templateCode: '',
    templateName: '',
    templateDescription: '',
    templateCategory: 'Web Development',
    templateType: 'Standard',
    templateStatus: 'Active',
    supportedProjectTypes: [],
    supportedTechnologies: [],
    templateStructure: {
      phases: [],
      milestones: [],
      tasks: []
    },
    resourceRequirements: {
      requiredRoles: [],
      teamSize: { min: 1, max: 5 },
      estimatedTotalHours: 0
    },
    qualityStandards: {
      codeReviewRequired: true,
      testingRequired: true,
      documentationRequired: true,
      deploymentApprovalRequired: true,
      qualityGates: []
    },
    configurationSettings: {
      environmentSetup: [],
      deploymentSettings: {
        deploymentType: 'Manual',
        deploymentSteps: [],
        rollbackPlan: ''
      },
      monitoringSettings: {
        monitoringRequired: true,
        metrics: [],
        alertingRules: []
      }
    },
    templateMetadata: {
      version: '1.0.0',
      usageCount: 0,
      successRate: 0,
      averageCompletionTime: 0,
      tags: [],
      complexity: 'Medium'
    }
  })

  const [loading, setLoading] = useState(false)

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleNestedInputChange = (parentField, childField, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value
      }
    }))
  }

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const addArrayItem = (field, newItem) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], newItem || '']
    }))
  }

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  const validateForm = () => {
    if (!formData.templateCode.trim()) {
      toast.error('Template Code is required')
      return false
    }
    if (!/^[A-Z0-9-]+$/.test(formData.templateCode.trim())) {
      toast.error('Template Code must contain only uppercase letters, numbers, and hyphens')
      return false
    }
    if (!formData.templateName.trim()) {
      toast.error('Template Name is required')
      return false
    }
    if (!formData.templateDescription.trim()) {
      toast.error('Template Description is required')
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      toast.info('Creating Project Template Master record...')
      const response = await projectTemplateMasterService.createProjectTemplateMaster({
        templateCode: formData.templateCode.toUpperCase(),
        templateName: formData.templateName,
        templateDescription: formData.templateDescription,
        templateCategory: formData.templateCategory,
        templateType: formData.templateType,
        templateStatus: formData.templateStatus,
        supportedProjectTypes: formData.supportedProjectTypes,
        supportedTechnologies: formData.supportedTechnologies,
        templateStructure: formData.templateStructure,
        resourceRequirements: formData.resourceRequirements,
        qualityStandards: formData.qualityStandards,
        configurationSettings: formData.configurationSettings,
        templateMetadata: formData.templateMetadata
      })

      if (response.success) {
        toast.success('Project Template Master record created successfully!', { autoClose: 2000 })
        navigate('/projects/project-template-master')
      } else {
        toast.error(response.message || 'Failed to create Project Template Master record')
      }
    } catch (error) {
      console.error('Error creating Project Template Master record:', error)
      if (error.response?.status === 400 && error.response.data?.message.includes('already exists')) {
        toast.error('Template Code already exists. Please use a different code.')
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to perform this action.')
      } else {
        toast.error('Failed to create Project Template Master record. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      templateCode: '',
      templateName: '',
      templateDescription: '',
      templateCategory: 'Web Development',
      templateType: 'Standard',
      templateStatus: 'Active',
      supportedProjectTypes: [],
      supportedTechnologies: [],
      templateStructure: {
        phases: [],
        milestones: [],
        tasks: []
      },
      resourceRequirements: {
        requiredRoles: [],
        teamSize: { min: 1, max: 5 },
        estimatedTotalHours: 0
      },
      qualityStandards: {
        codeReviewRequired: true,
        testingRequired: true,
        documentationRequired: true,
        deploymentApprovalRequired: true,
        qualityGates: []
      },
      configurationSettings: {
        environmentSetup: [],
        deploymentSettings: {
          deploymentType: 'Manual',
          deploymentSteps: [],
          rollbackPlan: ''
        },
        monitoringSettings: {
          monitoringRequired: true,
          metrics: [],
          alertingRules: []
        }
      },
      templateMetadata: {
        version: '1.0.0',
        usageCount: 0,
        successRate: 0,
        averageCompletionTime: 0,
        tags: [],
        complexity: 'Medium'
      }
    })
    toast.info('Form reset to default values')
  }

  const handleBack = () => {
    navigate('/projects/project-template-master')
  }

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Masters', path: '/projects?tab=masters', onClick: () => navigate('/projects?tab=masters') },
    { label: 'Project Template Master', path: '/projects/project-template-master', onClick: () => navigate('/projects/project-template-master') },
    { label: 'Create New', path: '/projects/project-template-master/new', isCurrent: true }
  ]

  const categoryOptions = [
    'Web Development',
    'Mobile Development',
    'Desktop Application',
    'API Development',
    'Data Science',
    'AI/ML',
    'DevOps',
    'Cloud Migration',
    'Legacy Modernization',
    'Support',
    'Maintenance'
  ]

  const typeOptions = [
    'Standard',
    'Custom',
    'Framework',
    'Library',
    'Integration',
    'Migration'
  ]

  const statusOptions = [
    'Active',
    'Inactive',
    'Deprecated',
    'Under Review'
  ]

  const projectTypeOptions = [
    'Internal',
    'External',
    'Support',
    'Development'
  ]

  const complexityOptions = [
    'Low',
    'Medium',
    'High',
    'Very High'
  ]

  const deploymentTypeOptions = [
    'Manual',
    'Automated',
    'Semi-automated'
  ]

  const helpContent = (
    <>
      <h3>What is Project Template Master?</h3>
      <p>Project Template Master lets you <strong>create predefined project templates and workflows</strong>. You can set category, type, supported project types and technologies, phases/milestones/tasks, resource requirements, quality standards, and deployment/monitoring settings.</p>
      <h3>Tabs</h3>
      <p><strong>Basic Info</strong> – Template code, name, description, category, type, status, supported project types and technologies.</p>
      <p><strong>Structure</strong> – Phases, milestones, tasks; resource requirements (roles, team size, estimated hours); quality standards (code review, testing, documentation, approval, quality gates).</p>
      <p><strong>Configuration</strong> – Environment setup, deployment type/steps/rollback, monitoring and alerting; template metadata (version, complexity, tags).</p>
      <h3>How to use</h3>
      <p>Fill required fields (code, name, description). Use <strong>Save</strong> to create. Use <strong>Reset</strong> to clear or navigate back to cancel.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Project Template Master Entry</h1>
          <p>Create and manage project template information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Project Template Master Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'basicInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('basicInfo')}
          >
            <FileText size={18} />
            <span>Basic Information</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'structure' ? 'active' : ''}`}
            onClick={() => setActiveTab('structure')}
          >
            <Settings size={18} />
            <span>Template Structure</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'resources' ? 'active' : ''}`}
            onClick={() => setActiveTab('resources')}
          >
            <Users size={18} />
            <span>Resource Requirements</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'quality' ? 'active' : ''}`}
            onClick={() => setActiveTab('quality')}
          >
            <CheckSquare size={18} />
            <span>Quality & Standards</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'configuration' ? 'active' : ''}`}
            onClick={() => setActiveTab('configuration')}
          >
            <Calendar size={18} />
            <span>Configuration</span>
          </button>
        </div>
          
        <form className="form-content">

          {/* Basic Information Tab */}
          {activeTab === 'basicInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Basic Information</h3>
              </div>
              
              {/* Row 1: Template Code, Template Name, Category, Type, Status */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="templateCode" className="form-label required">
                    Template Code
                  </label>
                  <input
                    id="templateCode"
                    type="text"
                    value={formData.templateCode}
                    onChange={(e) => handleInputChange('templateCode', e.target.value)}
                    className="form-input"
                    placeholder="e.g., WEB-REACT, MOB-NATIVE"
                    maxLength={20}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="templateName" className="form-label required">
                    Template Name
                  </label>
                  <input
                    id="templateName"
                    type="text"
                    value={formData.templateName}
                    onChange={(e) => handleInputChange('templateName', e.target.value)}
                    className="form-input"
                    placeholder="e.g., React Web Application"
                    maxLength={100}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="templateCategory" className="form-label required">
                    Template Category
                  </label>
                  <select
                    id="templateCategory"
                    value={formData.templateCategory}
                    onChange={(e) => handleInputChange('templateCategory', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Category</option>
                    {categoryOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="templateType" className="form-label required">
                    Template Type
                  </label>
                  <select
                    id="templateType"
                    value={formData.templateType}
                    onChange={(e) => handleInputChange('templateType', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Type</option>
                    {typeOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: Status */}
              <div className="grid-row-1">
                <div className="form-group">
                  <label htmlFor="templateStatus" className="form-label">
                    Status
                  </label>
                  <select
                    id="templateStatus"
                    value={formData.templateStatus}
                    onChange={(e) => handleInputChange('templateStatus', e.target.value)}
                    className="form-input"
                  >
                    {statusOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 3: Template Description */}
              <div className="form-group">
                <label htmlFor="templateDescription" className="form-label required">
                  Template Description
                </label>
                <textarea
                  id="templateDescription"
                  value={formData.templateDescription}
                  onChange={(e) => handleInputChange('templateDescription', e.target.value)}
                  className="form-input"
                  placeholder="Describe the template purpose, features, and use cases..."
                  rows={3}
                  maxLength={500}
                />
              </div>

              {/* Row 4: Supported Project Types */}
              <div className="form-group">
                <label className="form-label">Supported Project Types</label>
                <div className="checkbox-group" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {projectTypeOptions.map(option => (
                    <label key={option} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={formData.supportedProjectTypes.includes(option)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            addArrayItem('supportedProjectTypes', option)
                          } else {
                            removeArrayItem('supportedProjectTypes', formData.supportedProjectTypes.indexOf(option))
                          }
                        }}
                        style={{ margin: 0 }}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Row 5: Template Metadata */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="templateVersion" className="form-label">
                    Template Version
                  </label>
                  <input
                    id="templateVersion"
                    type="text"
                    value={formData.templateMetadata.version}
                    onChange={(e) => handleNestedInputChange('templateMetadata', 'version', e.target.value)}
                    className="form-input"
                    placeholder="e.g., 1.0.0"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="templateComplexity" className="form-label">
                    Template Complexity
                  </label>
                  <select
                    id="templateComplexity"
                    value={formData.templateMetadata.complexity}
                    onChange={(e) => handleNestedInputChange('templateMetadata', 'complexity', e.target.value)}
                    className="form-input"
                  >
                    {complexityOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Template Structure Tab */}
          {activeTab === 'structure' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Template Structure</h3>
                <p>Define the phases, milestones, and tasks for this template</p>
              </div>
              
              <div className="form-group">
                <label className="form-label">Template Phases</label>
                
                {/* Phases List */}
                <div className="resource-roles-container" style={{ 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '8px', 
                  padding: '16px',
                  backgroundColor: '#f8fafc',
                  minHeight: '200px'
                }}>
                  {formData.templateStructure.phases.length === 0 ? (
                    <div className="empty-state" style={{ 
                      textAlign: 'center', 
                      padding: '40px 20px',
                      color: '#64748b'
                    }}>
                      <Calendar size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>No Phases Added</h4>
                      <p style={{ margin: '0 0 16px 0', fontSize: '14px' }}>Add project phases for this template</p>
                      <button
                        type="button"
                        onClick={() => addArrayItem('templateStructure.phases', { 
                          phaseName: '', 
                          phaseDescription: '', 
                          estimatedDuration: 1, 
                          deliverables: [], 
                          dependencies: [], 
                          isOptional: false 
                        })}
                        className="btn-primary"
                        style={{ fontSize: '14px', padding: '8px 16px' }}
                      >
                        <Plus size={16} style={{ marginRight: '8px' }} />
                        Add First Phase
                      </button>
                    </div>
                  ) : (
                    <div className="resource-roles-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {formData.templateStructure.phases.map((phase, index) => (
                        <div key={index} className="resource-role-item" style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                        }}>
                          <div className="role-number" style={{
                            width: '24px',
                            height: '24px',
                            backgroundColor: '#292752',
                            color: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: '600',
                            flexShrink: 0
                          }}>
                            {index + 1}
                          </div>
                          
                          <input
                            type="text"
                            value={phase.phaseName}
                            onChange={(e) => {
                              const newPhases = [...formData.templateStructure.phases]
                              newPhases[index] = { ...newPhases[index], phaseName: e.target.value }
                              handleInputChange('templateStructure', { ...formData.templateStructure, phases: newPhases })
                            }}
                            className="form-input"
                            placeholder="Phase name (e.g., Planning, Development, Testing)"
                            style={{ 
                              flex: 1,
                              margin: 0,
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              padding: '8px 12px',
                              fontSize: '14px'
                            }}
                          />
                          
                          <input
                            type="number"
                            value={phase.estimatedDuration}
                            onChange={(e) => {
                              const newPhases = [...formData.templateStructure.phases]
                              newPhases[index] = { ...newPhases[index], estimatedDuration: parseInt(e.target.value) || 1 }
                              handleInputChange('templateStructure', { ...formData.templateStructure, phases: newPhases })
                            }}
                            className="form-input"
                            placeholder="Days"
                            style={{ 
                              width: '80px',
                              margin: 0,
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              padding: '8px 12px',
                              fontSize: '14px'
                            }}
                          />
                          
                          <button
                            type="button"
                            onClick={() => {
                              const newPhases = formData.templateStructure.phases.filter((_, i) => i !== index)
                              handleInputChange('templateStructure', { ...formData.templateStructure, phases: newPhases })
                            }}
                            className="btn-danger-small"
                            style={{
                              padding: '6px 12px',
                              fontSize: '12px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <span>Remove</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add Phase Button */}
                  {formData.templateStructure.phases.length > 0 && (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => addArrayItem('templateStructure.phases', { 
                          phaseName: '', 
                          phaseDescription: '', 
                          estimatedDuration: 1, 
                          deliverables: [], 
                          dependencies: [], 
                          isOptional: false 
                        })}
                        className="btn-secondary"
                        style={{
                          padding: '8px 16px',
                          fontSize: '14px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <Plus size={16} />
                        Add Another Phase
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Help Text */}
                <div style={{ 
                  marginTop: '8px', 
                  fontSize: '12px', 
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span>💡</span>
                  <span>Common phases: Planning, Development, Testing, Deployment, Maintenance</span>
                </div>
              </div>
            </div>
          )}

          {/* Resource Requirements Tab */}
          {activeTab === 'resources' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Resource Requirements</h3>
                <p>Define the team structure and resource needs for this template</p>
              </div>
              
              {/* Row 1: Team Size */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="teamSizeMin" className="form-label">
                    Minimum Team Size
                  </label>
                  <input
                    id="teamSizeMin"
                    type="number"
                    value={formData.resourceRequirements.teamSize.min}
                    onChange={(e) => handleNestedInputChange('resourceRequirements.teamSize', 'min', parseInt(e.target.value) || 1)}
                    className="form-input"
                    min="1"
                    max="20"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="teamSizeMax" className="form-label">
                    Maximum Team Size
                  </label>
                  <input
                    id="teamSizeMax"
                    type="number"
                    value={formData.resourceRequirements.teamSize.max}
                    onChange={(e) => handleNestedInputChange('resourceRequirements.teamSize', 'max', parseInt(e.target.value) || 5)}
                    className="form-input"
                    min="1"
                    max="20"
                  />
                </div>
              </div>

              {/* Row 2: Estimated Total Hours */}
              <div className="grid-row-1">
                <div className="form-group">
                  <label htmlFor="estimatedTotalHours" className="form-label">
                    Estimated Total Hours
                  </label>
                  <input
                    id="estimatedTotalHours"
                    type="number"
                    value={formData.resourceRequirements.estimatedTotalHours}
                    onChange={(e) => handleNestedInputChange('resourceRequirements', 'estimatedTotalHours', parseInt(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                    step="1"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Quality & Standards Tab */}
          {activeTab === 'quality' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Quality & Standards</h3>
                <p>Define quality requirements and standards for this template</p>
              </div>
              
              {/* Row 1: Quality Requirements */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.qualityStandards.codeReviewRequired}
                      onChange={(e) => handleNestedInputChange('qualityStandards', 'codeReviewRequired', e.target.checked)}
                      style={{ margin: 0 }}
                    />
                    <span>Code Review Required</span>
                  </label>
                </div>
                
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.qualityStandards.testingRequired}
                      onChange={(e) => handleNestedInputChange('qualityStandards', 'testingRequired', e.target.checked)}
                      style={{ margin: 0 }}
                    />
                    <span>Testing Required</span>
                  </label>
                </div>
              </div>

              {/* Row 2: Documentation and Deployment */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.qualityStandards.documentationRequired}
                      onChange={(e) => handleNestedInputChange('qualityStandards', 'documentationRequired', e.target.checked)}
                      style={{ margin: 0 }}
                    />
                    <span>Documentation Required</span>
                  </label>
                </div>
                
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.qualityStandards.deploymentApprovalRequired}
                      onChange={(e) => handleNestedInputChange('qualityStandards', 'deploymentApprovalRequired', e.target.checked)}
                      style={{ margin: 0 }}
                    />
                    <span>Deployment Approval Required</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Configuration Tab */}
          {activeTab === 'configuration' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Configuration Settings</h3>
                <p>Define deployment and monitoring configuration for this template</p>
              </div>
              
              {/* Row 1: Deployment Type */}
              <div className="grid-row-1">
                <div className="form-group">
                  <label htmlFor="deploymentType" className="form-label">
                    Deployment Type
                  </label>
                  <select
                    id="deploymentType"
                    value={formData.configurationSettings.deploymentSettings.deploymentType}
                    onChange={(e) => handleNestedInputChange('configurationSettings.deploymentSettings', 'deploymentType', e.target.value)}
                    className="form-input"
                  >
                    {deploymentTypeOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: Monitoring Settings */}
              <div className="grid-row-1">
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.configurationSettings.monitoringSettings.monitoringRequired}
                      onChange={(e) => handleNestedInputChange('configurationSettings.monitoringSettings', 'monitoringRequired', e.target.checked)}
                      style={{ margin: 0 }}
                    />
                    <span>Monitoring Required</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={handleReset}
              disabled={loading}
            >
              <RotateCcw size={18} />
              Reset
            </button>
            
            <button 
              type="button" 
              className="btn-primary" 
              onClick={handleSave}
              disabled={loading}
            >
              <Save size={18} />
              {loading ? 'Creating...' : 'Create Project Template'}
            </button>
          </div>
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Project Template Master – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default ProjectTemplateMasterForm
